import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { getPlannerTasks } from 'RestAPIs/planner-messages';

import { PlannerMessage, PlannerTaskTab } from '../types';
import {
  LEAVE_REQUEST_TASK_KEYS,
  LEAVE_UNREAD_TASK_KEYS,
  LEAVE_SEEN_TASK_KEYS,
  DUTY_CHANGE_TASK_KEYS,
  FLEX_NOTIFICATION_TASK_KEYS
} from '../constants';
import { usePlannerMessagesStore } from '../store';

type TabCounts = Record<PlannerTaskTab, number> & {
  leaveSeenCount?: number;
};

const useGetTabCounts = () => {
  const costCenterIds = usePlannerMessagesStore((s) => s.costCenterIds);
  const startDate = usePlannerMessagesStore((s) => s.startDate);
  const endDate = usePlannerMessagesStore((s) => s.endDate);

  return useQuery<TabCounts, AxiosError>({
    queryKey: ['plannerTaskTabCounts', costCenterIds, startDate, endDate],
    queryFn: async () => {
      const { data } = await getPlannerTasks({
        collection: 'open',
        costCenterIds,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
      return computeTabCounts(data);
    },
    placeholderData: {
      [PlannerTaskTab.ALL]: 0,
      [PlannerTaskTab.LEAVE_REQUESTS]: 0,
      [PlannerTaskTab.DUTY_CHANGES]: 0,
      [PlannerTaskTab.FLEX_NOTIFICATIONS]: 0,
      leaveSeenCount: 0
    }
  });
};

export const computeTabCounts = (
  tasks: PlannerMessage[]
): TabCounts => {
  const leaveKeys = new Set<string>(LEAVE_REQUEST_TASK_KEYS);
  const leaveUnreadKeys = new Set<string>(LEAVE_UNREAD_TASK_KEYS);
  const leaveSeenKeys = new Set<string>(LEAVE_SEEN_TASK_KEYS);
  const dutyKeys = new Set<string>(DUTY_CHANGE_TASK_KEYS);
  const flexKeys = new Set<string>(FLEX_NOTIFICATION_TASK_KEYS);

  const all = tasks.length;
  let leave = 0;
  let leaveSeen = 0;
  let duty = 0;
  let flex = 0;

  for (const task of tasks) {
    if (leaveKeys.has(task.taskDefinitionKey)) {
      if (leaveUnreadKeys.has(task.taskDefinitionKey)) {
        leave += 1;
      } else if (leaveSeenKeys.has(task.taskDefinitionKey)) {
        leaveSeen += 1;
      }
    } else if (dutyKeys.has(task.taskDefinitionKey)) {
      duty += 1;
    } else if (flexKeys.has(task.taskDefinitionKey)) {
      flex += 1;
    }
  }

  return {
    [PlannerTaskTab.ALL]: all,
    [PlannerTaskTab.LEAVE_REQUESTS]: leave,
    [PlannerTaskTab.DUTY_CHANGES]: duty,
    [PlannerTaskTab.FLEX_NOTIFICATIONS]: flex,
    leaveSeenCount: leaveSeen
  };
};

export default useGetTabCounts;
